import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { CalendarBlank as CalendarIcon } from '@phosphor-icons/react/dist/ssr/CalendarBlank';

import type { Amenity } from '@/types/v2/amenity';
import type { CreateReservationRequest, ReservationDocument } from '@/types/v2/reservation';
import type { StoredPaymentMethod } from '@/api/payment-methods';
import { createReservation } from '@/api/reservations';
import { getAmenities } from '@/api/amenities';
import { useUser } from '@/hooks/v2/use-user';
import { useConnectStatus } from '@/hooks/v2/use-connect-status';
import { getPresignedUploadUrl, uploadFileToPresignedUrl } from '@/utils/s3';
import { PaymentMethodSelector } from '@/components/common/payment-method-form';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { toast } from '@/components/core/v2/toaster';

function depositAmount(amenity: Amenity | undefined): number {
  if (!amenity?.deposit_amount) return 0;
  return Number(amenity.deposit_amount);
}

function totalFeeAmount(amenity: Amenity | undefined): number {
  const v = amenity?.total_fee_amount ?? amenity?.fee_amount;
  if (v == null) return 0;
  return Number(v);
}

function formatPaymentMethod(method: StoredPaymentMethod | null): string {
  if (!method) return 'your default saved payment method';

  if (method.type === 'us_bank_account') {
    return `${method.bank_name ?? 'Bank account'} ending in ${method.last4}`;
  }

  return `${method.brand ?? 'Card'} ending in ${method.last4}`;
}

function calcBlockEnd(startTime: Date, blockMinutes: number): Date {
  return dayjs(startTime).add(blockMinutes, 'minute').toDate();
}

const schema = zod.object({
  amenity_id: zod
    .union([zod.string(), zod.number()])
    .transform((v) => String(v))
    .refine((s) => s.length > 0, 'Amenity is required'),
  date: zod.date(),
  start_time: zod.union([zod.date(), zod.string()]),
  end_time: zod.union([zod.date(), zod.string()]),
  purpose: zod.string().optional(),
  special_requests: zod.string().optional(),
  alcohol_served: zod.boolean().optional(),
});

type Values = zod.infer<typeof schema>;

interface NewReservationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedAmenityId?: string | null;
}

export function NewReservationDialog({
  open,
  onClose,
  onSuccess,
  preselectedAmenityId,
}: NewReservationDialogProps): React.JSX.Element {
  const { user, community } = useUser();
  const queryClient = useQueryClient();
  const [documentFiles, setDocumentFiles] = React.useState<Record<string, File | null>>({});
  const [hasPaymentMethod, setHasPaymentMethod] = React.useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = React.useState<StoredPaymentMethod | null>(null);
  const [confirmDepositOpen, setConfirmDepositOpen] = React.useState(false);
  const [pendingReservationValues, setPendingReservationValues] = React.useState<Values | null>(null);

  const { data: amenitiesData, isLoading: isAmenitiesLoading } = useQuery({
    queryKey: ['amenities', community?.id, 'active'],
    queryFn: () => getAmenities(community?.id || '', { activeOnly: true }),
    enabled: !!community?.id && open,
  });

  const allAmenities = amenitiesData?.amenities ?? [];

  const { isReady: stripeReady, paymentsEnabled } = useConnectStatus(community?.id);

  const amenities = allAmenities;
  const noAmenitiesCreated = !isAmenitiesLoading && allAmenities.length === 0;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      amenity_id: '',
      date: dayjs().toDate(),
      start_time: dayjs().hour(9).minute(0).toDate(),
      end_time: dayjs().hour(10).minute(0).toDate(),
      purpose: '',
      special_requests: '',
      alcohol_served: false,
    },
  });

  const selectedAmenityId = watch('amenity_id');
  const selectedDate = watch('date');
  const selectedStartTime = watch('start_time');
  const selectedEndTime = watch('end_time');

  // Default to preselected or first amenity when amenities load
  React.useEffect(() => {
    if (amenities.length === 0) return;
    if (preselectedAmenityId) {
      const match = amenities.find((a) => String(a.id) === String(preselectedAmenityId));
      if (match) {
        setValue('amenity_id', match.id);
        return;
      }
    }
    if (!selectedAmenityId) {
      setValue('amenity_id', amenities[0].id);
    }
  }, [amenities, selectedAmenityId, preselectedAmenityId, setValue]);

  const selectedAmenity = React.useMemo(
    () => amenities.find((a) => a.id === selectedAmenityId),
    [amenities, selectedAmenityId],
  );

  const useTimeBlocks = Boolean(selectedAmenity?.time_block_duration);

  const minDurationMinutes = selectedAmenity?.reservation_min_duration ?? null;
  const maxDurationMinutes = selectedAmenity?.reservation_max_duration ?? null;

  const formatDuration = (minutes: number): string => {
    if (minutes >= 1440 && minutes % 1440 === 0) {
      const days = minutes / 1440;
      return days === 1 ? '1 day' : `${days} days`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) return hours === 1 ? '1 hour' : `${hours} hours`;
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  const depositFee = depositAmount(selectedAmenity);
  const totalFee = totalFeeAmount(selectedAmenity);
  const depositRequired = depositFee > 0;
  const paymentMethodLabel = formatPaymentMethod(defaultPaymentMethod);
  const requiredDocs = selectedAmenity?.required_documents ?? [];
  const needsDocuments = requiredDocs.length > 0;
  const hasAllDocuments = !needsDocuments || requiredDocs.every((type) => documentFiles[type]);

  React.useEffect(() => {
    setDocumentFiles({});
  }, [selectedAmenityId]);


  const shouldDisableDate = React.useCallback(
    (date: dayjs.Dayjs) => {
      if (!selectedAmenity) return false;

      if (selectedAmenity.seasonal_start_date) {
        const seasonStart = dayjs(selectedAmenity.seasonal_start_date);
        if (date.isBefore(seasonStart, 'day')) return true;
      }
      if (selectedAmenity.seasonal_end_date) {
        const seasonEnd = dayjs(selectedAmenity.seasonal_end_date);
        if (date.isAfter(seasonEnd, 'day')) return true;
      }

      if (selectedAmenity.availability_schedule) {
        const dayName = date.format('dddd').toLowerCase();
        const schedule =
          selectedAmenity.availability_schedule[
            dayName as keyof typeof selectedAmenity.availability_schedule
          ];
        if (!schedule) return true;
      }

      return false;
    },
    [selectedAmenity],
  );

  const daySchedule = React.useMemo(() => {
    if (!selectedAmenity?.availability_schedule || !selectedDate) return null;
    const dayName = dayjs(selectedDate).format('dddd').toLowerCase();
    return (
      selectedAmenity.availability_schedule[
        dayName as keyof typeof selectedAmenity.availability_schedule
      ] ?? null
    );
  }, [selectedAmenity, selectedDate]);

  const mutation = useMutation({
    mutationFn: (data: CreateReservationRequest) => createReservation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['blocked-slots'] });
      toast.success('Reservation created successfully');
      reset();
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create reservation');
    },
  });

  const submitReservation = async (data: Values) => {
    setConfirmDepositOpen(false);

    // Treat the user-picked date+time as wall-clock time in the community's timezone.
    // This ensures the backend availability check (which compares against the schedule
    // stored in community-local time) receives correct UTC values regardless of what
    // timezone the user's browser is in.
    const communityTz = community?.timezone || 'America/Chicago';

    let startDateTime: dayjs.Dayjs;
    let endDateTime: dayjs.Dayjs;

    {
      const dateStr = dayjs(data.date).format('YYYY-MM-DD');
      const startH = dayjs(data.start_time as Date).format('HH:mm');
      const endH = dayjs(data.end_time as Date).format('HH:mm');
      startDateTime = dayjs.tz(`${dateStr} ${startH}`, communityTz);
      endDateTime = dayjs.tz(`${dateStr} ${endH}`, communityTz);
      // If block crosses midnight (e.g. 9 PM + 5 hrs = 2 AM next day),
      // the end time string has no date context — advance by one day.
      if (!endDateTime.isAfter(startDateTime)) {
        endDateTime = endDateTime.add(1, 'day');
      }
    }

    let documents: ReservationDocument[] | undefined;
    if (needsDocuments && requiredDocs.length > 0) {
      documents = [];
      for (const docType of requiredDocs) {
        const file = documentFiles[docType];
        if (!file) continue;
        const { url, key } = await getPresignedUploadUrl(file);
        await uploadFileToPresignedUrl(url, file);
        documents.push({ type: docType, key, file_name: file.name });
      }
    }

    const requestData: CreateReservationRequest = {
      amenity_id: data.amenity_id,
      unit_id: user?.units?.[0]?.id,
      start_time: startDateTime.utc().format('YYYY-MM-DD HH:mm:ss'),
      end_time: endDateTime.utc().format('YYYY-MM-DD HH:mm:ss'),
      purpose: data.purpose || undefined,
      special_requests: data.special_requests || undefined,
      alcohol_served: data.alcohol_served || undefined,
      documents,
    };

    mutation.mutate(requestData, {
      onSettled: () => setPendingReservationValues(null),
    });
  };

  const onSubmit = async (data: Values) => {
    if (depositRequired && !hasPaymentMethod) {
      toast.error('A stored payment method is required for the deposit');
      return;
    }

    if (startTimeIsPast) {
      toast.error('Reservation must be for a future date and time');
      return;
    }

    if (depositRequired) {
      setPendingReservationValues(data);
      setConfirmDepositOpen(true);
      return;
    }

    await submitReservation(data);
  };

  const handleClose = () => {
    reset();
    setDocumentFiles({});
    setConfirmDepositOpen(false);
    setPendingReservationValues(null);
    onClose();
  };

  const setDocumentFile = (type: string, file: File | null) => {
    setDocumentFiles((prev) => ({ ...prev, [type]: file }));
  };

  // Compute duration reactively from both watched values
  const selectedDurationMinutes = React.useMemo(() => {
    if (useTimeBlocks || !selectedStartTime || !selectedEndTime || !selectedDate) return null;
    if (!(selectedStartTime instanceof Date) || !(selectedEndTime instanceof Date)) return null;
    const diff = dayjs(selectedEndTime).diff(dayjs(selectedStartTime), 'minute');
    return diff > 0 ? diff : null;
  }, [useTimeBlocks, selectedStartTime, selectedEndTime, selectedDate]);

  const startTimeIsPast = React.useMemo(() => {
    if (!selectedDate || !selectedStartTime || !(selectedStartTime instanceof Date)) return false;

    const communityTz = community?.timezone || 'America/Chicago';
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    const startH = dayjs(selectedStartTime).format('HH:mm');

    return !dayjs.tz(`${dateStr} ${startH}`, communityTz).isAfter(dayjs());
  }, [community?.timezone, selectedDate, selectedStartTime]);

  const meetsMinDuration =
    !minDurationMinutes ||
    useTimeBlocks ||
    selectedDurationMinutes === null ||
    selectedDurationMinutes >= minDurationMinutes;

  const meetsMaxDuration =
    !maxDurationMinutes ||
    useTimeBlocks ||
    selectedDurationMinutes === null ||
    selectedDurationMinutes <= maxDurationMinutes;

  const canSubmit =
    !mutation.isPending &&
    !isSubmitting &&
    Boolean(selectedAmenityId) &&
    !startTimeIsPast &&
    meetsMinDuration &&
    meetsMaxDuration &&
    (needsDocuments ? hasAllDocuments : true) &&
    (depositRequired ? hasPaymentMethod && stripeReady : true);

  return (
    <>
      <ProfessionalDialog
      open={open}
      onClose={handleClose}
      title="New Reservation"
      subtitle="Select an amenity and choose your preferred date and time"
      icon={<CalendarIcon size={22} />}
      maxWidth="sm"
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={mutation.isPending}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-reservation-form"
            variant="contained"
            disabled={!canSubmit}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {mutation.isPending
              ? 'Creating...'
              : depositRequired
                ? `Reserve & Pay Deposit ($${depositFee.toFixed(2)})`
                : 'Create Reservation'}
          </Button>
        </>
      }
    >
      <form id="new-reservation-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {/* ── Section: Amenity ── */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Amenity
            </Typography>
            <Controller
              name="amenity_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={Boolean(errors.amenity_id)}>
                  <InputLabel>Select amenity</InputLabel>
                  <Select {...field} label="Select amenity" disabled={isAmenitiesLoading || amenities.length === 0}>
                    {isAmenitiesLoading ? (
                      <MenuItem disabled value="">
                        Loading amenities...
                      </MenuItem>
                    ) : amenities.length === 0 ? (
                      <MenuItem disabled value="">
                        {noAmenitiesCreated ? 'No amenities have been created yet' : 'No amenities are available'}
                      </MenuItem>
                    ) : amenities.map((amenity) => (
                      <MenuItem key={amenity.id} value={amenity.id}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center', width: '100%' }}
                        >
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {amenity.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            {amenity.requires_approval && (
                              <Chip label="Approval" size="small" variant="outlined" />
                            )}
                            {depositAmount(amenity) > 0 && (
                              <Chip
                                label={`$${depositAmount(amenity).toFixed(2)} deposit`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {noAmenitiesCreated ? (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      No amenities have been created for this community yet. Please contact your community manager before making a reservation.
                    </Alert>
                  ) : null}
                  {errors.amenity_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.amenity_id.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>

          <Divider />

          {/* ── Section: Date & Time ── */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Date & Time
            </Typography>
            <Stack spacing={2}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date"
                    value={dayjs(field.value)}
                    onChange={(newValue) => {
                      if (newValue) field.onChange(newValue.toDate());
                    }}
                    minDate={dayjs()}
                    shouldDisableDate={shouldDisableDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        error: Boolean(errors.date),
                        helperText: errors.date?.message,
                      },
                    }}
                  />
                )}
              />

              {/* Time selection — block mode: pick any start, end auto-calculated */}
              {selectedAmenity && useTimeBlocks && (
                <Stack spacing={1}>
                  <Controller
                    name="start_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        label="Start Time"
                        value={field.value instanceof Date ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            field.onChange(newValue.toDate());
                            setValue('end_time', calcBlockEnd(newValue.toDate(), selectedAmenity.time_block_duration!));
                          }
                        }}
                        minTime={daySchedule ? dayjs(`${dayjs(selectedDate).format('YYYY-MM-DD')} ${daySchedule.start}`) : undefined}
                        maxTime={daySchedule ? dayjs(`${dayjs(selectedDate).format('YYYY-MM-DD')} ${daySchedule.end}`).subtract(selectedAmenity.time_block_duration!, 'minute') : undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            helperText: `End time is set automatically (${formatDuration(selectedAmenity.time_block_duration!)} block)`,
                          },
                        }}
                      />
                    )}
                  />
                  {/* Read-only end time display */}
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="End Time"
                        size="small"
                        fullWidth
                        value={field.value instanceof Date ? dayjs(field.value).format('h:mm A') : '—'}
                        InputProps={{ readOnly: true }}
                        helperText="Auto-calculated from start time"
                      />
                    )}
                  />
                </Stack>
              )}

              {/* Time selection — free-form mode */}
              {selectedAmenity && !useTimeBlocks && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Controller
                    name="start_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        label="Start Time"
                        value={field.value instanceof Date ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          if (newValue) field.onChange(newValue.toDate());
                        }}
                        minTime={
                          daySchedule
                            ? dayjs(
                                dayjs(selectedDate).format('YYYY-MM-DD') +
                                  ' ' +
                                  daySchedule.start,
                              )
                            : undefined
                        }
                        maxTime={
                          daySchedule
                            ? dayjs(
                                dayjs(selectedDate).format('YYYY-MM-DD') +
                                  ' ' +
                                  daySchedule.end,
                              )
                            : undefined
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: Boolean(errors.start_time),
                            helperText: errors.start_time?.message,
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        label="End Time"
                        value={field.value instanceof Date ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          if (newValue) field.onChange(newValue.toDate());
                        }}
                        minTime={
                          selectedStartTime instanceof Date
                            ? dayjs(selectedStartTime).add(15, 'minute')
                            : undefined
                        }
                        maxTime={
                          daySchedule
                            ? dayjs(
                                dayjs(selectedDate).format('YYYY-MM-DD') +
                                  ' ' +
                                  daySchedule.end,
                              )
                            : undefined
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: Boolean(errors.end_time),
                            helperText: errors.end_time?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
              )}

              {daySchedule && (
                <Typography variant="caption" color="text.secondary">
                  Available hours: {daySchedule.start} – {daySchedule.end}
                </Typography>
              )}

              {startTimeIsPast && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Reservation start time must be in the future. Please choose a later time.
                </Alert>
              )}

              {(minDurationMinutes || maxDurationMinutes) && !useTimeBlocks && (
                <Alert
                  severity={
                    selectedDurationMinutes !== null && (!meetsMinDuration || !meetsMaxDuration)
                      ? 'warning'
                      : 'info'
                  }
                  sx={{ py: 0.5 }}
                >
                  {selectedDurationMinutes !== null && !meetsMinDuration
                    ? `Your selection is ${formatDuration(selectedDurationMinutes)} — minimum booking is ${formatDuration(minDurationMinutes!)}. Please extend your end time.`
                    : selectedDurationMinutes !== null && !meetsMaxDuration
                    ? `Your selection is ${formatDuration(selectedDurationMinutes)} — maximum booking is ${formatDuration(maxDurationMinutes!)}. Please shorten your reservation.`
                    : [
                        minDurationMinutes ? `Min: ${formatDuration(minDurationMinutes)}` : null,
                        maxDurationMinutes ? `Max: ${formatDuration(maxDurationMinutes)}` : null,
                        selectedDurationMinutes !== null ? `Selected: ${formatDuration(selectedDurationMinutes)}` : null,
                      ].filter(Boolean).join(' · ')}
                </Alert>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* ── Section: Details ── */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Details
            </Typography>
            <Stack spacing={2}>
              {/* Max guests info — informational only, no input required */}
              {selectedAmenity?.max_guests && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  This amenity allows a maximum of <strong>{selectedAmenity.max_guests}</strong> guests.
                </Alert>
              )}

              <Controller
                name="purpose"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Purpose of Reservation" fullWidth size="small" />
                )}
              />

              <Controller
                name="special_requests"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Special Requests"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Any special requirements or notes..."
                  />
                )}
              />

              {selectedAmenity?.alcohol_question_enabled && (
                <Controller
                  name="alcohol_served"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">Will alcohol be served?</Typography>
                      }
                    />
                  )}
                />
              )}
            </Stack>
          </Box>

          {/* ── Section: Documents ── */}
          {needsDocuments && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Required Documents
                </Typography>
                <Stack spacing={1.5}>
                  {requiredDocs.map((docType) => (
                    <Box
                      key={docType}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: documentFiles[docType] ? 'success.main' : 'divider',
                        borderRadius: 1.5,
                        bgcolor: documentFiles[docType] ? 'success.50' : 'transparent',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {docType}
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        color={documentFiles[docType] ? 'success' : 'primary'}
                        sx={{ textTransform: 'none' }}
                      >
                        {documentFiles[docType]
                          ? documentFiles[docType]!.name
                          : 'Choose file'}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setDocumentFile(docType, f ?? null);
                          }}
                        />
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* ── Section: Payment ── */}
          {selectedAmenity && depositRequired && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Default Payment Method
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Deposits are charged to {paymentMethodLabel}. Change your default in payment settings.
                </Typography>
                {stripeReady ? (
                  <PaymentMethodSelector
                    communityId={community?.id ?? ''}
                    community={community}
                    onHasMethod={setHasPaymentMethod}
                    onDefaultMethodChange={setDefaultPaymentMethod}
                    selectable={false}
                  />
                ) : !paymentsEnabled ? (
                  <Alert severity="warning">
                    Payments are not enabled for this community. Contact your account administrator to enable payments before making deposit reservations.
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    Online payments are not configured for this community yet. A community manager must connect a Stripe account before reservations with deposits can be made.
                  </Alert>
                )}
              </Box>
            </>
          )}

          {/* ── Section: Fee Summary ── */}
          {selectedAmenity && (depositFee > 0 || totalFee > 0) && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Fee Summary
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Deposit
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {depositFee > 0 ? `$${depositFee.toFixed(2)}` : 'None'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Fee
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {totalFee > 0 ? `$${totalFee.toFixed(2)}` : 'None'}
                  </Typography>
                </Box>
              </Stack>
              {depositFee > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Deposit is collected at the time of reservation and forfeited if denied.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </form>
      </ProfessionalDialog>

      <Dialog
        open={confirmDepositOpen}
        onClose={() => !mutation.isPending && setConfirmDepositOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          Confirm Deposit Payment
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              You are about to create this reservation and pay the deposit now.
            </Typography>

            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" color="text.secondary">Amenity</Typography>
                  <Typography variant="body2" fontWeight={600} textAlign="right">
                    {selectedAmenity?.name ?? 'Reservation'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" color="text.secondary">Deposit Charged Now</Typography>
                  <Typography variant="body2" fontWeight={700}>
                    ${depositFee.toFixed(2)}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body2" fontWeight={600} textAlign="right">
                    {paymentMethodLabel}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Typography variant="caption" color="text.secondary">
              By confirming, you authorize an immediate charge of ${depositFee.toFixed(2)} to {paymentMethodLabel}.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmDepositOpen(false)}
            disabled={mutation.isPending}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!pendingReservationValues) return;
              void submitReservation(pendingReservationValues);
            }}
            disabled={mutation.isPending || !pendingReservationValues}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {mutation.isPending ? 'Processing...' : `Pay $${depositFee.toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
